import * as React from 'react'
import {connect} from 'react-redux'
import {Button, Card, CardBody, CardHeader, Col, Label, Row} from "reactstrap";
import {
    ConfiguratorProduct,
    ConfiguratorProductBuckets,
    ConfiguratorProductCategories,
    ConfiguratorProductOptions,
    ConfiguratorProductQuickcouplers,
    ConfiguratorProductQuickcouplers1,
    ConfiguratorProductTypes,
    ProductFamilyIconsInner,
    Product,
    UsedMainCategoriesInner
} from "src/services/api";
import {WithTranslation, withTranslation} from "react-i18next";
import {extractPrice, SelectedAlternativeProduct} from "App/services";
import {Currency, CurrencyProps, DATE_FORMAT, DetailPriceProps, Language} from "App/constants";
import {Component as BaseConf} from "Components/BaseConf";
import {Component as OptionsConf} from "Components/OptionsConf";
import {Component as QuickCouplerConf} from "Components/QuickCouplerConf";
import {Component as FullServiceConf} from "Components/FullService";
import {Component as ExtendedWarrantyConf} from "Components/ExtendedWarranty";
import {Component as RentConf} from "Components/RentConf";
import {Component as LeasingConf} from "Components/LeasingConf";
import {Component as AlternativeProducts} from "Components/AlternativeProducts";
import {Component as AlternativeUsed} from "Components/AlternativeUsed";
import {Basket, basketProductTypeChange} from "App/basket";
import {List} from "immutable";
import {selectOfferPageAction, selectContractPageAction} from "App/routes";
import {UserState} from "App/user";
import {startLogin} from "App/services/reducers/auth";
import moment from "moment";
import {Spinner} from "App/fragments/Spinner";
import {selectedLang} from "App/helper";
import {ImageCard} from "Components/ImageCard";
import {ReactNode} from "react";

interface StateToProps extends CurrencyProps, DetailPriceProps {
    category: UsedMainCategoriesInner
    subcategory: ProductFamilyIconsInner
    product: Product
    configuration: ConfiguratorProduct;
    alternatives: Map<number, SelectedAlternativeProduct>;
    basket: Basket;
    user: UserState | null;
}

interface DispatchToProps {
    onProductTypeChange: (type: ConfiguratorProductTypes) => void
    onContract: (basket: Basket, language: Language, currency: Currency) => void
    onOffer: (basket: Basket, language: Language, currency: Currency) => void
    onStartLogin: () => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
    onProductTypeChange: (type: ConfiguratorProductTypes) => dispatch(basketProductTypeChange(type)),
    onContract: (basket: Basket, language: Language, currency: Currency) => dispatch(selectContractPageAction()),
    onOffer: (basket: Basket, language: Language, currency: Currency) => dispatch(selectOfferPageAction()),
    onStartLogin: () => dispatch(startLogin())
});

class _Component extends React.PureComponent<StateToProps & DispatchToProps & WithTranslation, any> {

    getSelectedQuickCoupler(configuration: ConfiguratorProduct, language: Language, systemId: number | undefined, modelId: number | undefined): ReactNode {
        const system: ConfiguratorProductQuickcouplers1 | undefined = configuration.quickcouplers
            .find((item: ConfiguratorProductQuickcouplers1) => item.quickcouplersystemid === systemId);
        const model: ConfiguratorProductQuickcouplers | undefined = system && system.quickcouplers
            .find((item: ConfiguratorProductQuickcouplers) => item.quickcouplerid === modelId);
        return <div className="text-list-resume w-100">
            {system && system.name} - {model && (model.titleml && model.titleml[language] || model.name)}</div>;
    }

    render() {
        const {
            t, category, subcategory, product, configuration, alternatives, basket, i18n, currency,
            onProductTypeChange, onOffer, onContract, onStartLogin, detailPrice, user
        } = this.props;
        const chosenType: ConfiguratorProductTypes | undefined = configuration.types && configuration.types.find((type: ConfiguratorProductTypes) => type.typeid === basket.machineid);
        const basePrice: number | null = chosenType ? extractPrice(chosenType.price, currency) : null;

        const language: Language = selectedLang(i18n);

        return <React.Fragment>
            <ImageCard product={product} configuration={configuration} className={"d-none d-lg-block"}/>
            <Card className="mt-3">
                <BaseConf currency={currency} product={product} types={configuration.types}
                          onProductTypeChange={onProductTypeChange} chosenType={chosenType}/>
                {chosenType ?
                    <React.Fragment>
                        {
                            // Add options configurator section
                            configuration.categories ?
                                <OptionsConf currency={currency} categories={configuration.categories}
                                             options={basket.options}
                                             incompatibleOptions={chosenType.incompatibleoptions}
                                             chosenType={chosenType} detailPrice={detailPrice}/>
                                : null
                        }
                        {
                            // Add quickcouplers section
                            configuration.quickcouplers ?
                                <QuickCouplerConf currency={currency} systems={configuration.quickcouplers}
                                                  systemId={basket.quickCouplerSystemId}
                                                  modelId={basket.quickCouplerModelId}
                                                  selectedBuckets={basket.quickCouplerBuckets}
                                                  detailPrice={detailPrice}/>
                                : null
                        }
                        {
                            // Add fullservice configurator section
                            configuration.fullservice ?
                                <FullServiceConf currency={currency} fullService={basket.fullservice}
                                                 fullServiceDefinition={configuration.fullservice} user={user}/>
                                : null
                        }
                        {
                            // Add fullservice configurator section
                            configuration.extendedwarranty ?
                                <ExtendedWarrantyConf currency={currency}
                                                      extendedWarrantyDef={configuration.extendedwarranty}
                                                      extendedWarranty={basket.extendedwarranty} user={user}/>
                                : null
                        }
                        {
                            // Add rent configurator section
                            configuration.minrentpermonth ?
                                <RentConf currency={currency} rent={basket.rent} user={user}/>
                                : null
                        }
                        {
                            // Add leasing configurator section
                            configuration.leasingdurationmonths && configuration.leasinginitialpayments ?
                                <LeasingConf currency={currency}
                                             leasingdurationmonths={configuration.leasingdurationmonths}
                                             leasinginitialpayments={configuration.leasinginitialpayments}
                                             leasing={basket.leasing} user={user}/>
                                : null
                        }
                        <CardHeader className="px-5 d-flex align-items-center conf-title py-3">
                            <Label
                                className="title-normal-uppercase m-0 text-uppercase">{t('products.configuration.summary')}</Label>
                        </CardHeader>
                        <CardBody className="p-0">
                            <Row className="py-3 px-5 bodyslice no-gutters text-normal-details">
                                <Col
                                    className={"conf-entry d-flex flex-column text-left " + detailPrice ? "col-md-8" : "col"}>
                                    <span
                                        className="">{t(detailPrice ? 'products.configuration.typebaseprice' : 'products.configuration.typebase', {type: chosenType.name})}</span>
                                </Col>
                                {
                                    detailPrice ?
                                        <Col className="conf-entry col-md-4 d-flex flex-column text-right">
                                        <span className="">{t('products.configuration.price', {
                                            amount: basePrice,
                                            unit: currency
                                        })}</span>
                                        </Col> :
                                        null
                                }
                            </Row>
                            {
                                // Add selected options
                                basket.options.size ?
                                    <Row className="py-3 px-5 no-gutters text-normal-details">
                                        <div
                                            className="title-small-uppercase w-100">{t('products.configuration.extras')}</div>
                                        <ul className="u-list">
                                            {
                                                basket.options.map(function (list: List<number>, key: number) {
                                                    var category: ConfiguratorProductCategories | undefined = configuration.categories.find((item: ConfiguratorProductCategories) => item.categoryid === Number(key));
                                                    return list.map((id: number) => {
                                                        const option: ConfiguratorProductOptions | undefined = category ? category.options.find((option: ConfiguratorProductOptions) => option.optionid === id) : undefined;
                                                        return option ? <li key={key + '-' + id}
                                                                            className="text-list-resume">{option.titleml[language]}</li> : null;
                                                    })
                                                }).valueSeq()
                                            }
                                        </ul>
                                    </Row> :
                                    null
                            }

                            {
                                basket.quickCouplerSystemId && basket.quickCouplerModelId ?
                                    <Row className="py-3 px-5 no-gutters text-normal-details">
                                        <div
                                            className="title-small-uppercase w-100">{t('products.configuration.quickcoupler')}</div>
                                        {this.getSelectedQuickCoupler(configuration, language, basket.quickCouplerSystemId, basket.quickCouplerModelId)}<br/>
                                        <ul className="u-list">
                                            {
                                                configuration.quickcouplers && configuration.quickcouplers.length && basket.quickCouplerBuckets.length ?
                                                    basket.quickCouplerBuckets.map((bucket: ConfiguratorProductBuckets) => {
                                                        return <li key={bucket.bucketid} className="text-list-resume">{bucket.titleml && bucket.titleml[language] || bucket.name}
                                                        </li>;
                                                    }) : null
                                            }
                                        </ul>
                                    </Row> :
                                    null
                            }
                            <Row className="py-3 px-5 no-gutters totalslice">
                                <Col className="col-md-8 align-self-center">
                                    <div
                                        className="text-bold-details">{t('products.configuration.totalprice', {type: chosenType.name})}</div>
                                </Col>
                                <Col className="col-md-4 d-flex justify-content-end">
                                    <Spinner active={basket.syncState.pending}/>
                                    <div
                                        className={"pl-1 d-inline text-right text-bold-details " + (basket.syncState.pending ? "updating" : "")}>{t('products.configuration.price', {
                                        amount: basket.totalprice,
                                        unit: currency
                                    })}</div>
                                </Col>
                            </Row>
                            {user && !basket.specialdiscount ?
                                null :
                                <Row className="px-5 pb-3 no-gutters align-items-center">
                                    {basket.specialdiscount ?
                                        (basket.specialdiscount.remainingquantity || basket.specialdiscount.remainingquantity == 0 ?
                                            (basket.specialdiscount.remainingquantity == 0 ?
                                                <Col className="text-left">
                                                    <span
                                                        className="discount text-bold-overview align-middle">{t('products.configuration.specialdiscountterminated')}</span>
                                                </Col> :
                                                <Col className="text-left">
                                                    <span
                                                        className="discount text-bold-small-overview align-middle">{t('products.configuration.specialdiscountqty', {count: basket.specialdiscount.remainingquantity || 0})}</span>
                                                </Col>) :
                                            <Col className="text-left">
                                                <span
                                                    className="discount text-bold-small-overview align-middle">{t('products.configuration.specialdiscount', {enddate: moment(basket.specialdiscount.enddate).format(DATE_FORMAT)})}</span>
                                            </Col>) :
                                        <Col className="text-right">
                                        <span className="action pr-3 text-bold-small-overview align-middle"
                                              onClick={() => onStartLogin()}>{t('products.configuration.requestdiscount')}</span>
                                            <i className="action fal fa-lg fa-badge-percent align-middle"></i>
                                        </Col>
                                    }
                                </Row>
                            }
                            <Row className="pb-3 px-5 no-gutters bodyslice">
                                <Col className="col-md-12 d-flex flex-column text-left pb-2">
                                    <span
                                        className="text-small-details">{t('products.configuration.priceconditions')}</span>
                                </Col>
                            </Row>
                            <Row className="pb-3 pt-4 px-5 no-gutters align-items-center d-print-none">
                                <Col className="pl-0 pr-2 mb-2 col-md-6 align-self-center">
                                    <Button className="configure w-100 text-uppercase text-left button-height-48"
                                            onClick={() => onContract(basket, language, currency)} disabled={!basket}>
                                        <i className="far fa-check-circle pr-2 fa-lg"></i>{t('products.configuration.contract')}
                                    </Button>
                                </Col>
                                <Col className="pr-0 pl-2 mb-2 col-md-6 align-self-center">
                                    <Button className="advice w-100 text-uppercase text-left button-height-48"
                                            onClick={() => onOffer(basket, language, currency)}>
                                        <i className="far fa-arrow-circle-right pr-2 fa-lg"></i>{t('products.configuration.offer')}
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </React.Fragment>
                    : null}
            </Card>
            {
                // Add used products section
                configuration.alternativeused && configuration.alternativeused.length ?
                    <AlternativeUsed currency={currency} alternativeused={configuration.alternativeused}/>
                    : null
            }
            {
                // Add alternative products section
                configuration.alternativeproducts && configuration.alternativeproducts.length && alternatives && alternatives.size ?
                    <AlternativeProducts currency={currency} category={category} subcategory={subcategory}
                                         alternativeproducts={configuration.alternativeproducts}
                                         alternatives={alternatives} 
                                         standarddiscount={configuration.standarddiscount}
                                         divisionroundtonearest={configuration.divisionroundtonearest}
                                        />
                    : null
            }
        </React.Fragment>
    }
}

export const ConfiguratorDetailPage = connect(null, mapDispatchToProps)(withTranslation('common')(_Component));


// WEBPACK FOOTER //
// ./src/components/ConfiguratorDetail.tsx